import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BaseApiResponse,
  CdRequestStatus,
  ContractRequestDetailData,
  ContractRequestListItem,
  SendContractRequest,
} from "~/types/common";

const listKey = (statuses?: CdRequestStatus[]) =>
  ["business-contract-request-list", statuses ?? []] as const;
const detailKey = (seq: number) => ["business-contract-request", seq] as const;

/* ─── 목록 ─── */
const fetchRequestList = async (
  statuses?: CdRequestStatus[]
): Promise<ContractRequestListItem[]> => {
  const params = statuses && statuses.length > 0 ? { status: statuses } : {};
  const { data } = await privateHttp.get<BaseApiResponse<ContractRequestListItem[]>>(
    API_ENDPOINTS.BUSINESS_CONTRACT_REQUEST.LIST,
    { params, paramsSerializer: { indexes: null } }
  );
  return data.data ?? [];
};

export const useContractRequestList = (statuses?: CdRequestStatus[]) =>
  useQuery({
    queryKey: listKey(statuses),
    queryFn: () => fetchRequestList(statuses),
  });

/* ─── 상세 ─── */
const fetchRequestDetail = async (seq: number): Promise<ContractRequestDetailData> => {
  const { data } = await privateHttp.get<BaseApiResponse<ContractRequestDetailData>>(
    API_ENDPOINTS.BUSINESS_CONTRACT_REQUEST.DETAIL(seq)
  );
  return data.data;
};

export const useContractRequestDetail = (seq?: number) =>
  useQuery({
    queryKey: detailKey(seq ?? 0),
    queryFn: () => fetchRequestDetail(seq!),
    enabled: !!seq,
  });

/* ─── 발송 ─── */
export const useSendContractRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: SendContractRequest): Promise<number> => {
      const { data } = await privateHttp.post<BaseApiResponse<number>>(
        API_ENDPOINTS.BUSINESS_CONTRACT_REQUEST.SEND,
        req
      );
      // BE 는 실패도 HTTP 200 + body.statusCode(예: 4001 PIN 오류)로 응답한다.
      // axios 인터셉터는 HTTP status 만 보므로 여기서 body 코드를 반드시 검사해
      // 실패를 throw 해야 도장 PIN 오류 시 발송 모달이 닫히지 않고 실패로 처리된다.
      if (data.statusCode < 200 || data.statusCode >= 300) {
        throw new Error(data.message || `발송 실패 (code ${data.statusCode})`);
      }
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["business-contract-request-list"] });
    },
  });
};

/* ─── 취소 ─── */
export const useCancelContractRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestSeq: number) => {
      await privateHttp.post(API_ENDPOINTS.BUSINESS_CONTRACT_REQUEST.CANCEL(requestSeq));
    },
    onSuccess: (_, requestSeq) => {
      qc.invalidateQueries({ queryKey: ["business-contract-request-list"] });
      qc.invalidateQueries({ queryKey: detailKey(requestSeq) });
    },
  });
};

/* ─── 재발송 ─── */
export const useResendContractRequest = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestSeq: number) => {
      await privateHttp.post(API_ENDPOINTS.BUSINESS_CONTRACT_REQUEST.RESEND(requestSeq));
    },
    onSuccess: (_, requestSeq) => {
      qc.invalidateQueries({ queryKey: ["business-contract-request-list"] });
      qc.invalidateQueries({ queryKey: detailKey(requestSeq) });
    },
  });
};
